import lodash from 'lodash'

export type JSONValue = string | JSONData | JSONData[];

export interface JSONData {
    [key: string]: JSONValue;
}

const TAB_SPACING = 4

const spaces = (n: number): string => ' '.repeat(Math.max(0, n))

const isDict = (x: unknown): x is JSONData => lodash.isObject(x) && x !== null && !lodash.isArray(x)
const isList = (x: unknown): x is JSONData[] => lodash.isArray(x)

const json2tabString = (
    data: JSONValue,
    indent = 0,
    tabSpacing = TAB_SPACING,
    forcedWidth?: number,
// eslint-disable-next-line max-params
): string => {
    const pad = spaces(indent)

    if (isDict(data)) {
        const keys = Object.keys(data)
        const localWidth = forcedWidth ?? (keys.length ? Math.max(...keys.map((k) => k.length)) : 0)

        return keys
            .map((key) => {
                const value = data[key] ?? ''
                const head = `${pad}${key}`

                if (isDict(value) || isList(value)) {
                    return `${head}\n${json2tabString(
                        value,
                        indent + tabSpacing,
                        tabSpacing,
                    )}`
                }

                const gap = spaces(localWidth - (key?.length ?? 0) + tabSpacing)
                return `${head}${gap}${value}`
            })
            .join('\n')
    }

    if (isList(data)) {
        const sharedWidth =
            data.length && data.every(isDict)
                ? Math.max(
                    ...data.flatMap((d) => Object.keys(d).map((k) => k.length)),
                )
                : undefined

        return data
            .map((item) => {
                const bullet = `${pad}-`
                if (isDict(item) || isList(item)) {
                    return `${bullet}\n${json2tabString(
                        item,
                        indent + tabSpacing,
                        tabSpacing,
                        sharedWidth,
                    )}`
                }
                return `${bullet}${String(item)}`
            })
            .join('\n')
    }

    return `${pad}${String(data)}`
}

export const prettyPrint = (data: JSONValue): string => json2tabString(data)

export const isNumber = (value: string | number): boolean => !Number.isNaN(Number.parseFloat(`${value}`)) && Number.isFinite(Number(value))
